import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FlaskConical, RefreshCw } from 'lucide-react';
import { fetchHongguoConfig, fetchHongguoRun, startHongguoRun } from '../../../services/adminService';
import { ADMIN_ACCENT, EmptyBlock, ErrorNotice, Panel, PrimaryButton, SecondaryButton, StatusBadge } from '../adminUi.jsx';
import {
  formatHongguoUsd, formatHongguoValue, hongguoResultRows, isHongguoRunTerminal,
  parseHongguoSeriesIds, readHongguoAttempt, saveHongguoAttempt,
} from './hongguoDataUtils';
import './HongguoDataTab.css';

const STATUS_LABELS = {
  READY: '排队中', RUNNING: '运行中', SUCCEEDED: '已完成', FAILED: '失败',
  'TIMED-OUT': '运行超时', ABORTED: '已中止', ABORTING: '正在中止',
  'TIMING-OUT': '正在超时处理', TIMING_OUT: '正在超时处理', SUBMITTING: '正在提交',
  SUBMISSION_UNKNOWN: '提交结果不确定', RESTORED: '已恢复记录，尚未刷新',
};
const statusColor = status => status === 'SUCCEEDED' ? 'var(--color-success)'
  : ['FAILED', 'TIMED-OUT', 'ABORTED'].includes(status) ? 'var(--color-error)' : ADMIN_ACCENT;
const formatTime = value => {
  if (!value) return '未提供';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '未提供' : date.toLocaleString();
};

const RawJson = ({ value, label = '查看原始 JSON' }) => (
  <details>
    <summary>{label}</summary>
    <pre className="hongguo-json font-mono-num">{JSON.stringify(value, null, 2)}</pre>
  </details>
);

const HongguoDataTab = () => {
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState('');
  const [input, setInput] = useState('');
  const [consent, setConsent] = useState(false);
  const [clearConsent, setClearConsent] = useState(false);
  const [attempt, setAttempt] = useState(readHongguoAttempt);
  const [result, setResult] = useState(null);
  const [recoveryId, setRecoveryId] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const mounted = useRef(false);
  const configRequest = useRef(null);
  const runRequest = useRef(null);
  // Ref lock is synchronous: a second click in the same render cannot issue a paid POST.
  const actionLock = useRef(false);

  const maxIds = typeof config?.maxSeriesIds === 'number' ? Math.max(1, Math.min(10, config.maxSeriesIds)) : 10;
  const parsed = parseHongguoSeriesIds(input, maxIds);
  const needsReview = attempt && !isHongguoRunTerminal(attempt.status);
  const rows = result?.status === 'SUCCEEDED' ? hongguoResultRows(result.items, result.seriesIds) : [];

  const loadConfig = useCallback(async () => {
    configRequest.current?.abort();
    const controller = new AbortController();
    configRequest.current = controller;
    setConfigLoading(true);
    setConfigError('');
    try {
      const data = await fetchHongguoConfig({ signal: controller.signal });
      if (mounted.current && !controller.signal.aborted) setConfig(data);
    } catch (cause) {
      if (mounted.current && !controller.signal.aborted) setConfigError(cause?.message || '配置状态加载失败。');
    } finally {
      if (mounted.current && !controller.signal.aborted) setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadConfig();
    return () => {
      mounted.current = false;
      configRequest.current?.abort();
      runRequest.current?.abort();
    };
  }, [loadConfig]);

  const applyRun = (data, context) => {
    if (!data || typeof data !== 'object' || !data.status) throw new Error('服务端未返回有效运行状态，请核对 Apify 控制台。');
    const next = { ...data, seriesIds: context.seriesIds || [], requestId: data.requestId || context.requestId, runId: data.runId || context.runId || null };
    setAttempt(next);
    saveHongguoAttempt(next);
    // Keep the last successful dataset visible while a later run is pending or fails.
    if (next.status === 'SUCCEEDED') setResult(next);
    setRecoveryId(next.runId || '');
  };

  const handleStart = async () => {
    if (actionLock.current || needsReview) return;
    if (parsed.error) { setError(parsed.error); return; }
    if (!config?.configured) { setError('请先在密钥配置中设置 Apify API Token。'); return; }
    if (!consent) { setError('请先确认本次测试可能产生 Apify 费用。'); return; }
    if (!globalThis.crypto?.randomUUID) { setError('当前浏览器无法生成安全请求 ID，请使用 HTTPS 或 localhost 后重试。'); return; }
    actionLock.current = true;
    const controller = new AbortController();
    runRequest.current = controller;
    const context = { requestId: crypto.randomUUID(), seriesIds: parsed.seriesIds, runId: null, status: 'SUBMITTING' };
    setBusy('start');
    setError('');
    setConsent(false);
    setClearConsent(false);
    setAttempt(context);
    saveHongguoAttempt(context);
    try {
      const data = await startHongguoRun({ seriesIds: context.seriesIds, requestId: context.requestId, confirmCost: true }, { signal: controller.signal });
      if (mounted.current && !controller.signal.aborted) applyRun(data, context);
    } catch (cause) {
      if (mounted.current && !controller.signal.aborted) {
        const uncertain = { ...context, status: 'SUBMISSION_UNKNOWN' };
        setAttempt(uncertain);
        saveHongguoAttempt(uncertain);
        setError(`${cause?.message || '启动请求失败。'} 本页不会自动重试。请先核对 Apify 控制台是否已创建运行，再决定是否新建测试。`);
      }
    } finally {
      actionLock.current = false;
      if (mounted.current && !controller.signal.aborted) setBusy('');
    }
  };

  const handleRefresh = async (runId) => {
    const id = String(runId || '').trim();
    if (actionLock.current) return;
    if (!/^[a-zA-Z0-9_-]{1,80}$/.test(id)) { setError('请输入有效的 Apify run ID（字母、数字、下划线或连字符）。'); return; }
    actionLock.current = true;
    const controller = new AbortController();
    runRequest.current = controller;
    setBusy('refresh');
    setError('');
    try {
      const data = await fetchHongguoRun(id, { signal: controller.signal });
      if (mounted.current && !controller.signal.aborted) {
        const sameRun = attempt?.runId === id || (data?.requestId && data.requestId === attempt?.requestId);
        applyRun(data, { runId: id, requestId: sameRun ? attempt?.requestId : '', seriesIds: sameRun ? attempt?.seriesIds : [] });
        setClearConsent(false);
      }
    } catch (cause) {
      if (mounted.current && !controller.signal.aborted) setError(`${cause?.message || '状态刷新失败。'} 已保留上一次结果；刷新不会新建付费运行。`);
    } finally {
      actionLock.current = false;
      if (mounted.current && !controller.signal.aborted) setBusy('');
    }
  };

  const clearAttempt = () => {
    if (actionLock.current || !clearConsent) return;
    setAttempt(null);
    saveHongguoAttempt(null);
    setConsent(false);
    setClearConsent(false);
    setError('');
  };

  return (
    <div className="hongguo-admin space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold flex items-center gap-2"><FlaskConical size={16} style={{ color: ADMIN_ACCENT }} />红果数据测试</h2>
          <p className="hongguo-muted mt-1">通过 Apify 查询剧集详情，仅管理员可用。此测试不受顶部项目过滤影响。</p>
        </div>
        <SecondaryButton onClick={loadConfig} disabled={configLoading} className="inline-flex items-center gap-1.5"><RefreshCw size={13} />{configLoading ? '读取配置中…' : '刷新配置'}</SecondaryButton>
      </div>

      {configError ? <div role="alert"><ErrorNotice message={configError} /></div> : null}
      <Panel title="连接与费用">
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge label={configLoading ? '检查配置中' : configError ? '配置状态未知' : config?.configured ? 'Token 已配置' : 'Token 未配置'} color={config?.configured && !configError ? 'var(--color-success)' : ADMIN_ACCENT} />
          <code className="break-all">{config?.actorId || 'spider_studio/hongguo-series-detail'}</code>
        </div>
        <p className="mt-2">Apify 使用独立额度或账单，可能产生费用，不扣本站积分。运行是否成功、实际费用以 Apify 返回和账单为准。</p>
        <p className="hongguo-muted mt-1">服务端单次费用上限参数：{formatHongguoUsd(config?.maxTotalChargeUsd)}。该限制不作为实际账单金额保证。</p>
        {!configLoading && !configError && !config?.configured ? <p className="mt-2">请前往 <a href="/admin?tab=secrets">密钥配置</a> 设置 <strong>Apify API Token</strong>，返回后刷新配置。此页面不读取或输入 Token。</p> : null}
      </Panel>

      <Panel title="提交测试" sub="手动发起 · 每次最多 10 个 ID">
        <label htmlFor="hongguo-series-ids" className="block font-medium mb-1.5">红果剧集 ID</label>
        <textarea id="hongguo-series-ids" className="hongguo-input font-mono-num" rows={3} value={input}
          onChange={event => { setInput(event.target.value); setConsent(false); }}
          placeholder="粘贴剧集 ID，用逗号、空格或换行分隔" spellCheck={false}
          aria-describedby="hongguo-id-help" aria-invalid={input.trim() !== '' && Boolean(parsed.error)} disabled={Boolean(busy)} />
        <p id="hongguo-id-help" className="hongguo-muted mt-1">仅支持 1–30 位数字 ID，自动去重并按字符串保留精度。不解析分享链接或剧名。最多 {maxIds} 个。</p>
        {input.trim() ? <p className="mt-1" style={{ color: parsed.error ? 'var(--color-error)' : 'var(--color-text-secondary)' }}>{parsed.error || `已识别 ${parsed.seriesIds.length} 个唯一 ID。`}</p> : null}
        <label className="hongguo-check mt-2"><input type="checkbox" checked={consent} disabled={Boolean(busy) || Boolean(needsReview)} onChange={event => setConsent(event.target.checked)} /><span>我确认本次测试可能消耗 Apify 额度或产生独立费用。</span></label>
        <PrimaryButton onClick={handleStart} disabled={Boolean(busy) || Boolean(needsReview) || !config?.configured || configLoading || Boolean(configError) || !consent || Boolean(parsed.error)}>
          {busy === 'start' ? '正在提交，请勿重复操作…' : '开始新测试（可能产生费用）'}
        </PrimaryButton>
        <p className="hongguo-muted mt-2">不会自动提交或自动轮询；启动后请手动刷新状态。切换标签页不会终止 Apify 中的运行。</p>
      </Panel>

      {error ? <div role="alert"><ErrorNotice message={error} /></div> : null}

      <Panel title="运行状态" right={attempt?.runId ? <SecondaryButton onClick={() => handleRefresh(attempt.runId)} disabled={Boolean(busy)}>{busy === 'refresh' ? '刷新中…' : '刷新状态'}</SecondaryButton> : null}>
        {attempt ? <div className="space-y-3">
          <div role="status" aria-live="polite"><StatusBadge label={`${STATUS_LABELS[attempt.status] || '未知状态'} · ${attempt.status || '未提供'}`} color={statusColor(attempt.status)} /></div>
          <dl className="hongguo-metadata">
            <div><dt>Run ID</dt><dd className="font-mono-num">{formatHongguoValue(attempt.runId)}</dd></div>
            <div><dt>请求 ID（用于核对重复提交）</dt><dd className="font-mono-num">{formatHongguoValue(attempt.requestId)}</dd></div>
            <div><dt>开始时间</dt><dd>{formatTime(attempt.startedAt)}</dd></div>
            <div><dt>结束时间</dt><dd>{formatTime(attempt.finishedAt)}</dd></div>
            <div><dt>Apify 返回费用</dt><dd>{formatHongguoUsd(attempt.usageTotalUsd)}</dd></div>
            <div><dt>本次请求的剧集 ID</dt><dd className="font-mono-num">{attempt.seriesIds?.join('、') || '未提供'}</dd></div>
          </dl>
          {Array.isArray(attempt.warnings) && attempt.warnings.length ? <div className="hongguo-notice" role="status">{attempt.warnings.map((warning, index) => <p key={index}>{typeof warning === 'string' ? warning : JSON.stringify(warning)}</p>)}</div> : null}
          {needsReview ? <div className="hongguo-notice">
            <p>当前记录尚未确认结束。若提交超时或没有 Run ID，请先在 Apify 控制台核对运行与结果，避免重复创建付费测试。下方只能恢复本服务已登记的 Run ID；提交结果不确定时，仅从控制台复制 Run ID 可能无法在此查询。</p>
            <label className="hongguo-check mt-1"><input type="checkbox" checked={clearConsent} disabled={Boolean(busy)} onChange={event => setClearConsent(event.target.checked)} /><span>我已核对 Apify 控制台，了解新建测试可能再次计费。</span></label>
            <SecondaryButton onClick={clearAttempt} disabled={Boolean(busy) || !clearConsent}>清除本次记录，允许新建测试</SecondaryButton>
            <p className="hongguo-muted mt-1">仅清除本页记录，不会取消正在运行的 Apify 任务。</p>
          </div> : null}
          {['FAILED', 'TIMED-OUT', 'ABORTED'].includes(attempt.status) ? <p role="status">本次运行未成功；无法据此判断剧集是否存在，也不会自动重新运行。{attempt.status === 'FAILED' ? '请检查 Apify 运行日志。' : ''}</p> : null}
        </div> : <p className="hongguo-muted">尚未发起测试。下方也可查询此服务实例已登记的 Run ID。</p>}
        <div className="mt-4 pt-3 border-t" style={{ borderColor: 'var(--color-border-secondary)' }}>
          <label htmlFor="hongguo-run-id" className="block font-medium mb-1.5">恢复已有运行（只读查询）</label>
          <div className="flex flex-wrap items-center gap-2">
            <input id="hongguo-run-id" className="hongguo-input flex-1 min-w-0" style={{ maxWidth: 420 }} value={recoveryId} onChange={event => setRecoveryId(event.target.value)} placeholder="输入已有 Run ID" spellCheck={false} disabled={Boolean(busy)} aria-describedby="hongguo-run-help" />
            <SecondaryButton onClick={() => handleRefresh(recoveryId)} disabled={Boolean(busy) || !recoveryId.trim()}>查询已有运行</SecondaryButton>
          </div>
          <p id="hongguo-run-help" className="hongguo-muted mt-1">仅查询本服务实例最近登记的运行；服务重启或记录过期后可能无法恢复。不会创建新运行。</p>
        </div>
      </Panel>

      <Panel title="查询结果" sub="只读 · 保留原始字段">
        {result?.status === 'SUCCEEDED' ? <>
          <p className="hongguo-muted mb-2">结果所属 Run ID：{result.runId}。仅直接展示 series.title / episodeCount / playCount；字段不存在时显示“未提供”。</p>
          <p className="hongguo-muted mb-3">热度、点赞、评分、评论、收藏：未提供已确认的指标口径。播放量不等于热度；其他字段请查看原始 JSON，不自行换算或补零。</p>
          {rows.length ? <div className="overflow-x-auto">
            <table className="hongguo-results"><caption className="sr-only">红果剧集详情原始字段摘要</caption><thead><tr><th scope="col">剧集 ID</th><th scope="col">条目状态</th><th scope="col">剧名 title</th><th scope="col">集数 episodeCount</th><th scope="col">播放量 playCount</th></tr></thead>
              <tbody>{rows.map(row => <tr key={row.key}><td className="font-mono-num break-all">{row.seriesId || '未提供'}</td><td>{row.status}</td><td>{row.title}</td><td>{row.episodeCount}</td><td>{row.playCount}</td></tr>)}</tbody>
            </table>
          </div> : <EmptyBlock text="运行成功，但数据集为空" hint="空结果不代表剧集不存在，也不代表各项指标为零。" />}
          {rows.some(row => row.missing) ? <p className="hongguo-muted mt-2">“未返回匹配记录”仅说明数据集中没有相同 seriesId，可能是字段缺失或采集未返回，不代表剧集不存在。</p> : null}
          <RawJson value={result.items || []} label="展开原始数据集 JSON（含失败条目与未识别字段）" />
        </> : <EmptyBlock text={result ? '尚无成功的数据集结果' : '暂无查询结果'} hint="运行完成后手动刷新；这里只展示真实返回的数据，不提供示例数据。" />}
      </Panel>
    </div>
  );
};

export default HongguoDataTab;
